import { produce } from 'immer';
import * as React from 'react';
import {
  Action,
  ActionWithPayload,
  AnswerSelectionAction,
  Dispatch,
  FieldAction,
} from '../types/action';
import { Device } from '../types/device';
import { FlexAssessmentContextAction } from '../types/flex-assessment-context-action';
import { FlexAssessmentProviderProps } from '../types/flex-assessment-provider-props';
import { RegistrationFieldName } from '../types/registration-field-name';
import { State } from '../types/state';
import { AssessmentQuestion, Field } from '../types/types';
import { uniqueId } from '../utils/generate-uuid';
import { getUrlParam } from '../utils/query-vars';
import { getUtmParams } from '../utils/utm-params';

const initialAppState: State = {
  questionSet: [],
  userSelections: [],
  userAnswers: {},
  notifications: [],
  utm: getUtmParams(),
  accessCode: getUrlParam('access_code') ?? undefined,
  registeredUserId: getUrlParam('user_id') ?? undefined,
  email: {
    value: '',
    isValid: undefined,
  },
  password: {
    value: '',
    isValid: undefined,
  },
  firstName: {
    value: getUrlParam('given_name') ?? '',
    isValid: undefined,
  },
  lastName: {
    value: '',
    isValid: undefined,
  },
  streetAddress1: {
    value: '',
    isValid: undefined,
  },
  streetAddress2: {
    value: '',
    isValid: undefined,
  },
  city: {
    value: '',
    isValid: undefined,
  },
  state: {
    value: '',
    isValid: undefined,
  },
  zipCode: {
    value: '',
    isValid: undefined,
  },
  country: {
    value: 'United States',
    isValid: true,
  },
  telephone: {
    value: '',
    isValid: undefined,
  },
  acceptedLegals: {
    value: false,
    isValid: false,
  },
  allowSms: {
    value: false,
    isValid: false,
  },
  accessCodeConfig: {},
  orgConfig: {},
  user: undefined,
  inAppAssessmentConfig: {
    isInApp: false,
    isOnboarding: true,
  },
  userId: getUrlParam('user_id') ?? undefined,
  device: (getUrlParam('device') as Device) ?? Device.browser,
  alias: undefined,
  isB2BCampaignAssessment: false,
  assessmentResult: {
    flexScore: 0,
    scoreDetails: {
      subCategoryScores: {
        stressManagement: 0,
        anxietyManagement: 0,
        burnoutManagement: 0,
        resilienceSkills: 0,
      },
    },
    completionTimestamp: undefined,
  },
  subCategoryMaxScores: {
    stressManagement: 0,
    anxietyManagement: 0,
    burnoutManagement: 0,
    resilienceSkills: 0,
  },
  assessmentId: undefined,
  entrancePath: undefined,
};

const FlexAssessmentContext = React.createContext<
  { state: State; dispatch: Dispatch } | undefined
>(undefined);

function flexAssessmentContextReducer(
  state: State,
  action: Action | ActionWithPayload | FieldAction | AnswerSelectionAction
): State {
  switch (action.type) {
    case FlexAssessmentContextAction.showNotification:
      const a = action as ActionWithPayload;
      const doesMessageExist = state.notifications.every(({ message }) => {
        return message === a.payload.message;
      });
      if (state.notifications.length === 0 || !doesMessageExist) {
        return {
          ...state,
          notifications: [
            ...state.notifications,
            {
              isShowing: true,
              message: a.payload.message,
              type: a.payload.type,
              id: uniqueId(),
            },
          ],
        };
      }
      return state;
    case FlexAssessmentContextAction.dismissNotification: {
      const a = action as ActionWithPayload;
      const notifications = state.notifications.filter(
        (notification) => notification.id !== a.payload.id
      );
      return {
        ...state,
        notifications: notifications,
      };
    }
    case FlexAssessmentContextAction.setInAppAssessmentConfig: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.inAppAssessmentConfig = a.payload;
      });
    }
    case FlexAssessmentContextAction.setAlias: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.alias = a.payload;
      });
    }
    case FlexAssessmentContextAction.setOrgConfig: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.orgConfig = a.payload;
      });
    }
    case FlexAssessmentContextAction.setAccessCodeConfig: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.accessCodeConfig = a.payload;
      });
    }
    case FlexAssessmentContextAction.setAccessCode: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.accessCode = a.payload;
      });
    }
    case FlexAssessmentContextAction.setQuestions: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.questionSet = a.payload;
      });
    }
    case FlexAssessmentContextAction.setAssessmentId: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.assessmentId = a.payload;
      });
    }
    case FlexAssessmentContextAction.selectAnswer: {
      const a = action as AnswerSelectionAction;
      const index = state.questionSet.findIndex(
        (question) => question.id === a.questionId
      );
      const questionClone: AssessmentQuestion = structuredClone(
        state.questionSet[index]
      );
      questionClone.answerOptions.forEach((answer) => {
        answer.selected = answer.id === a.answerId;
      });

      return produce(state, (newState): void => {
        newState.userSelections[index] = questionClone;
        newState.userAnswers[questionClone.id] = a.answerId;
      });
    }
    case FlexAssessmentContextAction.setAssessmentResult: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.assessmentResult = a.payload;
      });
    }
    case FlexAssessmentContextAction.setFieldValue: {
      const fieldAction = action as FieldAction;
      const field = state[fieldAction.field] as Field;
      return {
        ...state,
        [fieldAction.field]: {
          ...field,
          value: fieldAction.value,
          isValid: fieldAction.isValid,
        },
      };
    }
    case FlexAssessmentContextAction.setUserAuthenticationObject: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.user = a.payload;
      });
    }
    case FlexAssessmentContextAction.saveAllUserInput: {
      const a = action as ActionWithPayload;
      const payload: Record<keyof RegistrationFieldName, Field> = a.payload;
      return produce(state, (newState: Partial<State>): void => {
        for (const field in payload) {
          if (payload[field].isValid) {
            // @ts-ignore
            newState[field as RegistrationFieldName] = {
              value: payload[field].value,
              isValid: true,
            };
          }
        }
      });
    }
    case FlexAssessmentContextAction.setInB2BCampaignAssessment: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.isB2BCampaignAssessment = a.payload;
      });
    }
    case FlexAssessmentContextAction.setSubCategoryMaxScores: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.subCategoryMaxScores = a.payload;
      });
    }
    case FlexAssessmentContextAction.setEntrancePath: {
      const a = action as ActionWithPayload;
      return produce(state, (newState): void => {
        newState.entrancePath = a.payload;
      });
    }
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

export function FlexAssessmentContextProvider({
  children,
}: FlexAssessmentProviderProps) {
  const [state, dispatch] = React.useReducer(
    flexAssessmentContextReducer,
    initialAppState
  );

  const value = { state, dispatch };
  return (
    <FlexAssessmentContext.Provider value={value}>
      {children}
    </FlexAssessmentContext.Provider>
  );
}

export function useFlexAssessmentContext() {
  const context = React.useContext(FlexAssessmentContext);
  if (context === undefined) {
    throw new Error(
      'useFlexAssessmentContext must be used within a FlexAssessmentContextProvider'
    );
  }
  return context;
}
